import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../api/api';
import toast from 'react-hot-toast';
import { ReactComponent as DigntecLogo } from '../../Assets/images/DigntecLogo.svg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import '../contract/contractSign.css';

export default function CustomerIntake() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [storeName, setStoreName] = useState('');

  // Form fields
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [contactSame, setContactSame] = useState(true);
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [town, setTown] = useState('');
  const [state, setState] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [storePhone, setStorePhone] = useState('');
  const [taxId, setTaxId] = useState('');
  const [openDate, setOpenDate] = useState('');
  const [screenCount, setScreenCount] = useState(3);
  const [shippingSame, setShippingSame] = useState(true);
  const [shippingAddress, setShippingAddress] = useState({
    street: '', street2: '', city: '', state: '', zipcode: '', recipientName: '', recipientPhone: '',
  });

  useEffect(() => {
    api.get(`/intake/verify/${token}`)
      .then(res => {
        const s = res.data.store || {};
        setStoreName(res.data.storeName || s.name || '');
        if (s.ownerName) setOwnerName(s.ownerName);
        if (s.ownerEmail) setOwnerEmail(s.ownerEmail);
        if (s.ownerPhone) setOwnerPhone(s.ownerPhone);
        if (s.contactEmail) { setContactEmail(s.contactEmail); setContactSame(false); }
        if (s.contactPhone) { setContactPhone(s.contactPhone); setContactSame(false); }
        if (s.address1) setAddress1(s.address1);
        if (s.address2) setAddress2(s.address2);
        if (s.town) setTown(s.town);
        if (s.state) setState(s.state);
        if (s.zipcode) setZipcode(s.zipcode);
        if (s.storePhone) setStorePhone(s.storePhone);
        if (s.taxId) setTaxId(s.taxId);
        if (s.openDate) setOpenDate(new Date(s.openDate).toISOString().slice(0, 10));
        if (s.screenCount) setScreenCount(s.screenCount);
        setLoading(false);
      })
      .catch(() => { setExpired(true); setLoading(false); });
  }, [token]);

  const handleSubmit = async () => {
    if (!ownerName.trim()) return toast.error('Please enter the owner name');
    if (!ownerEmail.trim()) return toast.error('Please enter the owner email');

    setSubmitting(true);
    try {
      await api.post(`/intake/submit/${token}`, {
        ownerName: ownerName.trim(),
        ownerEmail: ownerEmail.trim(),
        ownerPhone: ownerPhone.trim(),
        contactEmail: contactSame ? ownerEmail.trim() : contactEmail.trim(),
        contactPhone: contactSame ? ownerPhone.trim() : contactPhone.trim(),
        address1, address2, town, state, zipcode, storePhone, taxId,
        screenCount, openDate: openDate || undefined,
        shippingAddress: shippingSame
          ? { sameAsStore: true }
          : { sameAsStore: false, ...shippingAddress },
      });
      setSubmitted(true);
      toast.success('Information submitted!');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Submission failed');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="cs-root"><div className="cs-loading">Loading...</div></div>;

  if (expired) return (
    <div className="cs-root">
      <div className="cs-card" style={{ textAlign: 'center', padding: 40 }}>
        <h2>Link Expired</h2>
        <p>This registration link has expired or is invalid.</p>
        <p>Please contact <a href="mailto:support@digntec.com">support@digntec.com</a> or call (201) 357-7222.</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div className="cs-root">
      <div className="cs-card" style={{ textAlign: 'center', padding: 40 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: '#22c55e', mb: 2 }} />
        <h2>Registration Complete!</h2>
        <p>Thank you, <strong>{ownerName}</strong>.</p>
        <p>Your store information has been submitted. Our team will begin preparing your digital menu board setup.</p>
        <p style={{ color: '#888', fontSize: 14, marginTop: 16 }}>You can now close this page.</p>
        <p style={{ color: '#aaa', fontSize: 12, marginTop: 20 }}>
          Questions? Call (201) 357-7222 or email support@digntec.com
        </p>
      </div>
    </div>
  );

  return (
    <div className="cs-root">
      <div className="cs-card">
        {/* Header */}
        <div className="cs-header">
          <DigntecLogo style={{ width: 120, height: 'auto' }} />
          <h2>Tous Les Jours</h2>
          <p style={{ margin: '4px 0 0', color: '#888', fontSize: 14 }}>Digital Menu Board — Store Registration</p>
        </div>

        {storeName && (
          <div className="cs-info">
            <p><strong>{storeName}</strong></p>
          </div>
        )}

        {/* Owner Information */}
        <div className="cs-section">
          <h3>Owner Information <span style={{ color: '#e53935', fontSize: 12 }}>*</span></h3>
          <input className="cs-name-input" placeholder="Owner Full Name *" value={ownerName}
            onChange={e => setOwnerName(e.target.value)} />
          <input className="cs-name-input" placeholder="Owner Email *" type="email" value={ownerEmail}
            onChange={e => setOwnerEmail(e.target.value)} style={{ marginTop: 8 }} />
          <input className="cs-name-input" placeholder="Owner Phone" type="tel" value={ownerPhone}
            onChange={e => setOwnerPhone(e.target.value)} style={{ marginTop: 8 }} />
        </div>

        {/* Contact Information */}
        <div className="cs-section">
          <h3>Contact Information</h3>
          <div className="cs-radio-group">
            <label className="cs-radio">
              <input type="radio" checked={contactSame} onChange={() => setContactSame(true)} />
              Same as owner
            </label>
            <label className="cs-radio">
              <input type="radio" checked={!contactSame} onChange={() => setContactSame(false)} />
              Different contact person
            </label>
          </div>
          {!contactSame && (
            <div className="cs-shipping-form">
              <input placeholder="Contact Email" type="email" value={contactEmail}
                onChange={e => setContactEmail(e.target.value)} />
              <input placeholder="Contact Phone" type="tel" value={contactPhone}
                onChange={e => setContactPhone(e.target.value)} />
            </div>
          )}
        </div>

        {/* Store Address */}
        <div className="cs-section">
          <h3>Store Address</h3>
          <div className="cs-shipping-form">
            <input placeholder="Street Address" value={address1}
              onChange={e => setAddress1(e.target.value)} />
            <input placeholder="Apt, Suite, Unit (optional)" value={address2}
              onChange={e => setAddress2(e.target.value)} />
            <div style={{ display: 'flex', gap: 8 }}>
              <input placeholder="City" value={town} style={{ flex: 1 }}
                onChange={e => setTown(e.target.value)} />
              <input placeholder="State" value={state} style={{ width: 60 }}
                onChange={e => setState(e.target.value)} />
              <input placeholder="Zip" value={zipcode} style={{ width: 80 }}
                onChange={e => setZipcode(e.target.value)} />
            </div>
            <input placeholder="Store Phone" type="tel" value={storePhone}
              onChange={e => setStorePhone(e.target.value)} />
          </div>
        </div>

        {/* Shipping Address */}
        <div className="cs-section">
          <h3>Device Shipping Address</h3>
          <div className="cs-radio-group">
            <label className="cs-radio">
              <input type="radio" checked={shippingSame} onChange={() => setShippingSame(true)} />
              Same as store address
            </label>
            <label className="cs-radio">
              <input type="radio" checked={!shippingSame} onChange={() => setShippingSame(false)} />
              Different address
            </label>
          </div>
          {!shippingSame && (
            <div className="cs-shipping-form">
              <input placeholder="Street Address" value={shippingAddress.street}
                onChange={e => setShippingAddress(a => ({ ...a, street: e.target.value }))} />
              <input placeholder="Apt, Suite, Unit (optional)" value={shippingAddress.street2}
                onChange={e => setShippingAddress(a => ({ ...a, street2: e.target.value }))} />
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="City" value={shippingAddress.city} style={{ flex: 1 }}
                  onChange={e => setShippingAddress(a => ({ ...a, city: e.target.value }))} />
                <input placeholder="State" value={shippingAddress.state} style={{ width: 60 }}
                  onChange={e => setShippingAddress(a => ({ ...a, state: e.target.value }))} />
                <input placeholder="Zip" value={shippingAddress.zipcode} style={{ width: 80 }}
                  onChange={e => setShippingAddress(a => ({ ...a, zipcode: e.target.value }))} />
              </div>
              <input placeholder="Recipient Name" value={shippingAddress.recipientName}
                onChange={e => setShippingAddress(a => ({ ...a, recipientName: e.target.value }))} />
              <input placeholder="Recipient Phone" value={shippingAddress.recipientPhone}
                onChange={e => setShippingAddress(a => ({ ...a, recipientPhone: e.target.value }))} />
            </div>
          )}
        </div>

        {/* Business Details */}
        <div className="cs-section">
          <h3>Business Details</h3>
          <div className="cs-shipping-form">
            <input placeholder="Tax ID / EIN (optional)" value={taxId}
              onChange={e => setTaxId(e.target.value)} />
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Opening Date</label>
                <input type="date" value={openDate}
                  onChange={e => setOpenDate(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 4 }}>Number of Screens</label>
                <input type="number" min="1" max="20" value={screenCount}
                  onChange={e => setScreenCount(e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 14 }} />
              </div>
            </div>
          </div>
        </div>

        {/* Privacy Notice */}
        <div style={{ background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: 8, padding: 16, margin: '16px 0', fontSize: 11, color: '#888', lineHeight: 1.6 }}>
          <p style={{ fontWeight: 600, color: '#666', margin: '0 0 4px' }}>Privacy & Data Protection</p>
          <p style={{ margin: 0 }}>
            The information you provide will be used solely for the purpose of setting up and managing your
            Tous Les Jours digital menu board service. Your data will not be shared with, sold to, or disclosed
            to any third parties. All information is stored securely in accordance with applicable data protection laws.
          </p>
        </div>

        {/* Submit */}
        <button className="cs-submit" onClick={handleSubmit}
          disabled={submitting || !ownerName.trim() || !ownerEmail.trim()}>
          {submitting ? 'Submitting...' : 'Submit Registration'}
        </button>

        <p className="cs-footer">
          Digntec LLC · 15 Veterans Way, Edgewater, NJ 07020<br/>
          Phone: (201) 357-7222 · support@digntec.com
        </p>
      </div>
    </div>
  );
}
